import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'

import {
  PageWrapper,
  Loader,
  PreviousPage,
  AccountDetails,
  AccountInfo,
  PageFooter,
  AccountLinks,
  AccountImpacts,
  ClaimAccount,
  UnSdgs,
  FullWidthSection,
} from 'src/components'
import { useAccount, useAccountImpacts, useAccountProducts, useGetAccountSimpleId } from 'src/hooks'
import { LeftDivider, TopDivider } from '../product/product.styled'
import { LayoutContainer } from 'src/components/layout-container'
import { useEffect } from 'react'
import { uselAccountFirstRedirect } from 'src/contexts'
import { Box, Divider, Typography, useTheme } from '@mui/material'
import { FlaskIcon } from 'src/assets/icons/v2/FlaskIcon'
import { getBaseUrl, toAbs } from 'src/utils/base-url.util'
import { getUniqueUnSdgsFromImpacts } from 'src/utils/impact.util'
import { ensureProductHomePath } from 'src/utils'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { FairtradeFonts } from 'src/templates/fairtrade/fairtrade-fonts'
import { FairtradeAccountTemplate } from 'src/templates/fairtrade/account'

export default function Account() {
  const [t, i18n] = useTranslation()
  const { accountId } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const { setIsRedirected } = uselAccountFirstRedirect()
  const location = useLocation()

  useEffect(() => {
    setIsRedirected(true)
  }, [])

  const isUuid = (id: string) =>
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id)

  const { data: accountSimpleId, isLoading: accountSimpleIdIsLoading } = useGetAccountSimpleId(
    isUuid(accountId) ? accountId : null,
  )

  useEffect(() => {
    if (isUuid(accountId) && !accountSimpleIdIsLoading && accountSimpleId) {
      navigate(location.pathname.replace(accountId, accountSimpleId), { replace: true })
    }
  }, [accountSimpleIdIsLoading, accountSimpleId])

  const { data: account, isLoading: isAccountLoading } = useAccount(accountId, i18n.language)
  const withourProduct = !location.state?.productId
  const { data: products, isLoading } = useAccountProducts(accountId, withourProduct)
  const { data: accountImpacts, isLoading: isAccountImpactsLoading } = useAccountImpacts(
    i18n.language,
    accountId,
  )

  if (isAccountLoading || !account || isLoading || isAccountImpactsLoading) {
    return <Loader />
  }

  // --- SEO (Helmet) ---
  const base = getBaseUrl()
  const canonical = `${base}${window.location.pathname}`
  const image = account?.image ? toAbs(account.image) : `${base}/android-chrome-512x512.png`

  const accTitle = `${account.account_holder} – Trackgood Journey`
  const accDesc = `Explore the journey of ${account.account_holder}. Discover their products and impacts on Trackgood Journey.`

  const otherProducts = location.state?.productId
    ? products?.products.filter((p) => p.simple_id.toString() !== location.state?.productId)
    : products?.products

  const currentProduct = location.state?.productId
    ? products?.products.find((p) => p.simple_id.toString() === location.state?.productId)
    : null

  const allProducts = currentProduct ? [currentProduct, ...otherProducts] : products.products
  const filteredAccountImpacts = accountImpacts.filter((item) => item.account_page_show === true)

  const unSdgs = getUniqueUnSdgsFromImpacts(filteredAccountImpacts)

  const isFairtradeTemplate =
    account?.journey_page_template_id === JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE
  const previousFrom = ensureProductHomePath(location.state?.from)

  return (
    <LayoutContainer>
      <Helmet>
        <title id="index__pages-account__title__l90">{accTitle}</title>
        <meta id="index__pages-account__meta__l91" name="description" content={accDesc} />
        <link id="index__pages-account__link__l92" rel="canonical" href={canonical} />

        <meta id="index__pages-account__meta__l94" property="og:title" content={accTitle} />
        <meta id="index__pages-account__meta__l95" property="og:description" content={accDesc} />
        <meta id="index__pages-account__meta__l96" property="og:url" content={canonical} />
        <meta id="index__pages-account__meta__l97" property="og:image" content={image} />

        <meta id="index__pages-account__meta__l99" name="twitter:title" content={accTitle} />
        <meta id="index__pages-account__meta__l100" name="twitter:description" content={accDesc} />
        <meta id="index__pages-account__meta__l101" name="twitter:image" content={image} />
      </Helmet>

      {isFairtradeTemplate && <FairtradeFonts />}

      {!isFairtradeTemplate && (account?.alternative_color || account?.ai_generated) && (
        <TopDivider id="index__pages-account__topdivider__l105" bgColor={account.alternative_color}>
          {account?.ai_generated && (
            <>
              <Typography
                id="index__pages-account__typography__l108"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  mt: '2px',
                }}
                textAlign={'center'}
              >
                {t('ai-generated-label')}
              </Typography>
              <FlaskIcon size={14} />
            </>
          )}
        </TopDivider>
      )}

      {isFairtradeTemplate && (
        <LeftDivider
          id="index__pages-account__leftdivider__l135"
          bgColor={account?.alternative_color || account?.primary_color}
        />
      )}

      {isFairtradeTemplate ? (
        <FairtradeAccountTemplate
          account={account}
          filteredAccountImpacts={filteredAccountImpacts}
          unSdgs={unSdgs}
          allProducts={allProducts}
        />
      ) : (
        <PageWrapper>
          <Box
            id="index__pages-account__box__l127"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: '8px',
              position: 'relative',
              px: '20px',
              pt: '20px',
            }}
          >
            {previousFrom ? (
              <Box
                id="index__pages-account__box__l159"
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <PreviousPage to={previousFrom} />
              </Box>
            ) : (
              <div id="index__pages-account__div__l138" style={{ visibility: 'hidden' }} />
            )}
            {account?.ai_generated && <ClaimAccount />}
          </Box>

          <FullWidthSection
            id="index__pages-account__fullwidthsectionst__l105"
            idInner="index__pages-account__fullwidthsectioninner__l105"
            sxInner={{
              px: '20px',
            }}
          >
            <AccountInfo
              image={account.image_medium || account.image}
              country={account.country}
              region={account.region}
              accountName={account.account_holder}
              walletAddress={account.wallet_address}
              showWalletAddress={account.show_wallet_address}
            />
          </FullWidthSection>

          <FullWidthSection
            id="index__pages-account__fullwidthsectionst__l106"
            idInner="index__pages-account__fullwidthsectioninner__l106"
          >
            <AccountLinks
              socials={account.socials || {}}
              website={account.website}
              email={account.email}
            />
          </FullWidthSection>

          {filteredAccountImpacts.length > 0 && (
            <>
              <Divider id="index__pages-account__divider__l164" />
              <FullWidthSection
                id="index__pages-account__fullwidthsectionst__l107"
                idInner="index__pages-account__fullwidthsectioninner__l107"
                sx={{
                  pt: '20px',
                  pb: '15px',
                }}
              >
                <AccountImpacts
                  impacts={filteredAccountImpacts}
                  walletAddress={account.wallet_address}
                  showWalletAddress={account.show_wallet_address}
                  showBcInfo={account.show_bc_info}
                  accountImage={account.image_medium || account.image}
                  primaryColor={account.primary_color}
                  alternativeColor={account.alternative_color}
                />
              </FullWidthSection>

              {account?.show_unsgd_account_page && unSdgs.length > 0 && (
                <>
                  <Divider id="index__pages-account__divider__l169" />

                  <FullWidthSection
                    id="index__pages-account__fullwidthsectionst__l108"
                    idInner="index__pages-account__fullwidthsectioninner__l108"
                    sx={{
                      py: '20px',
                    }}
                  >
                    <UnSdgs sdgs={unSdgs} />
                  </FullWidthSection>
                </>
              )}
              <Divider id="index__pages-account__divider__l175" />
            </>
          )}

          <AccountDetails
            media={account.media}
            allProducts={allProducts}
            description={account.description}
          />
        </PageWrapper>
      )}

      <PageFooter accountId={accountId} />
    </LayoutContainer>
  )
}
